import React, { useState, useEffect, useRef } from "react";
import { translationsApi } from "../../services/api";
import { useToast } from "./Toast";

const TranslationsPage: React.FC = () => {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<"pt" | "en">("pt");
  const [ptJson, setPtJson] = useState("");
  const [enJson, setEnJson] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadTranslations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTranslations = async () => {
    setLoading(true);
    try {
      const response = await translationsApi.getAll();
      if (response.success && response.data) {
        setPtJson(JSON.stringify(response.data.pt, null, 2));
        setEnJson(JSON.stringify(response.data.en, null, 2));
      } else {
        showToast(response.message || "Failed to load translations", "error");
      }
    } catch {
      showToast("Failed to load translations", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const pt = JSON.parse(ptJson);
      const en = JSON.parse(enJson);

      const response = await translationsApi.update({ pt, en });
      if (response.success) {
        showToast(response.message || "Translations saved successfully");
      } else {
        showToast(response.message || "Failed to save", "error");
      }
    } catch {
      showToast("Invalid JSON format. Please check your syntax.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const content = event.target?.result as string;
        const parsed = JSON.parse(content);
        JSON.stringify(parsed, null, 2);

        if (activeTab === "pt") {
          setPtJson(JSON.stringify(parsed, null, 2));
        } else {
          setEnJson(JSON.stringify(parsed, null, 2));
        }
        showToast(`JSON file loaded for ${activeTab.toUpperCase()}`, "info");
      } catch {
        showToast("Invalid JSON file", "error");
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleDownload = () => {
    const content = activeTab === "pt" ? ptJson : enJson;
    const blob = new Blob([content], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `translations_${activeTab}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const formatJson = () => {
    try {
      if (activeTab === "pt") {
        setPtJson(JSON.stringify(JSON.parse(ptJson), null, 2));
      } else {
        setEnJson(JSON.stringify(JSON.parse(enJson), null, 2));
      }
      showToast("JSON formatted");
    } catch {
      showToast("Cannot format — invalid JSON", "error");
    }
  };

  if (loading) {
    return (
      <div className="admin-loader">
        <div className="admin-spinner" />
      </div>
    );
  }

  return (
    <div>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Translations</h1>
        <p className="admin-page-subtitle">
          Manage bilingual content for your portfolio (PT / EN)
        </p>
      </div>

      <div className="admin-card">
        <div className="admin-json-upload" onClick={() => fileInputRef.current?.click()}>
          <i className="fa-solid fa-file-import" />
          <div className="admin-json-upload-text">
            <h4>Upload JSON File</h4>
            <p>
              Click to upload a JSON file for the {activeTab.toUpperCase()} translations
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            className="admin-file-input"
            onChange={handleFileUpload}
          />
        </div>

        <div className="admin-tabs">
          <button
            className={`admin-tab${activeTab === "pt" ? " active" : ""}`}
            onClick={() => setActiveTab("pt")}
          >
            <i className="fa-solid fa-flag" style={{ marginRight: "0.5rem" }} />
            Portuguese (PT)
          </button>
          <button
            className={`admin-tab${activeTab === "en" ? " active" : ""}`}
            onClick={() => setActiveTab("en")}
          >
            <i className="fa-solid fa-flag" style={{ marginRight: "0.5rem" }} />
            English (EN)
          </button>
        </div>

        <textarea
          className="admin-json-editor"
          value={activeTab === "pt" ? ptJson : enJson}
          onChange={(e) =>
            activeTab === "pt" ? setPtJson(e.target.value) : setEnJson(e.target.value)
          }
          spellCheck={false}
        />

        <div className="admin-btn-group" style={{ marginTop: "1.5rem" }}>
          <button
            className="admin-btn admin-btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            <i className={`fa-solid ${saving ? "fa-spinner fa-spin" : "fa-save"}`} />
            <span>{saving ? "Saving..." : "Save Translations"}</span>
          </button>
          <button className="admin-btn admin-btn-secondary" onClick={handleDownload}>
            <i className="fa-solid fa-download" />
            <span>Download {activeTab.toUpperCase()}</span>
          </button>
          <button className="admin-btn admin-btn-secondary" onClick={formatJson}>
            <i className="fa-solid fa-align-left" />
            <span>Format JSON</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TranslationsPage;
